import {
	IconCheck,
	IconClose,
	IconKeyboardArrowDown,
	IconKeyboardArrowUp,
} from '@shipstation/adl-foundations-icons-react';
import { Data, UI, Utils } from 'common';
import React, { useEffect, useRef, useState } from 'react';

const { AdlButton } = UI;
const { BillingPlans } = Utils;
const { PlanIds } = Data;

type Cell = boolean | string | number;

interface FeatureRow {
	label: string;
	basic: Cell;
	professional: Cell;
	multiLocation: Cell;
}

interface FeatureGroup {
	category: string;
	items: FeatureRow[];
}

const PLAN_FEATURES: FeatureGroup[] = [
	{
		category: 'Sending and printing',
		items: [
			{
				label: 'Print shipping labels',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Print stamps',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Print return labels',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Send Certified Mail',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Certified mail return receipts',
				basic: false,
				professional: 'Individual',
				multiLocation: 'Individual',
			},
			{
				label: 'Send Registered Mail',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Monthly stamp limit',
				basic: '25',
				professional: 'Unlimited',
				multiLocation: 'Unlimited',
			},
			{
				label: 'Monthly label limit',
				basic: '10',
				professional: 'Unlimited',
				multiLocation: 'Unlimited',
			},
			{
				label: 'Reprint misprints at no extra cost',
				basic: false,
				professional: true,
				multiLocation: true,
			},
		],
	},
	{
		category: 'Carrier access and savings',
		items: [
			{
				label: 'UPS, USPS, and FedEx access',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'International carriers',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Discounted carrier rates',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Consignment tool',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Scheduled package pickups',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Free postage and supplies on signup',
				basic: true,
				professional: true,
				multiLocation: true,
			},
		],
	},
	{
		category: 'Workflow and efficiency tools savings',
		items: [
			{
				label: 'Quickbooks Online integration (coming soon)',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Address book and contact management',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Contact groupings and segmentation',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Address book integrations',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Multi-address batch label printing',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Multi-sheet printing',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Envelope and label customization',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Mailing and shipping configuration presets',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: '3rd-party printing and sending',
				basic: false,
				professional: true,
				multiLocation: true,
			},
		],
	},
	{
		category: 'Tracking and visibility',
		items: [
			{
				label: 'Centralized tracking',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Recipient tracking/notification emails',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Print history',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Advanced mail and ship analytics',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Cost reference codes',
				basic: false,
				professional: true,
				multiLocation: true,
			},
		],
	},
	{
		category: 'Team and location management',
		items: [
			{
				label: 'Locations included',
				basic: '1',
				professional: '1',
				multiLocation: '2',
			},
			{
				label: 'Multi-location account linking',
				basic: false,
				professional: false,
				multiLocation: true,
			},
			{
				label: 'Number of users',
				basic: '1',
				professional: '3',
				multiLocation: '10',
			},
			{
				label: 'Purchase additional users',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Role permissions',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'User roles and location permissions',
				basic: false,
				professional: false,
				multiLocation: true,
			},
			{
				label: 'Advanced reporting by location',
				basic: false,
				professional: false,
				multiLocation: true,
			},
		],
	},
	{
		category: 'Support and account services',
		items: [
			{
				label: 'Help Center',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Chatbot',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Email support',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Live chat',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Phone support',
				basic: false,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Dedicated account manager',
				basic: false,
				professional: false,
				multiLocation: true,
			},
			{
				label: 'Supplies store discounts',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'Mobile and desktop app access',
				basic: true,
				professional: true,
				multiLocation: true,
			},
			{
				label: 'AI optimization assistance',
				basic: false,
				professional: true,
				multiLocation: true,
			},
		],
	},
];

const COLUMNS = [
	{ key: 'basic' as const, name: 'Basic', planId: PlanIds.Basic402 },
	{
		key: 'professional' as const,
		name: 'Professional',
		planId: PlanIds.Professional403,
	},
	{
		key: 'multiLocation' as const,
		name: 'Multi-Location',
		planId: PlanIds.MultiLocation404,
	},
];

const CheckIcon = () => (
	<IconCheck
		className="plan-features-table__check"
		size={16}
		aria-hidden="true"
	/>
);

const ChevronIcon = ({ direction }: { direction: 'up' | 'down' }) =>
	direction === 'up' ? (
		<IconKeyboardArrowUp
			className="plan-features-table__chevron"
			size={16}
			aria-hidden="true"
		/>
	) : (
		<IconKeyboardArrowDown
			className="plan-features-table__chevron"
			size={16}
			aria-hidden="true"
		/>
	);

const XIcon = () => (
	<IconClose className="plan-features-table__x" size={16} aria-hidden="true" />
);

const renderCell = (value: Cell) => {
	if (value === true) return <CheckIcon />;
	if (value === false) return <XIcon />;
	if (value === '' || value == null) return <span aria-hidden="true">—</span>;
	return <span>{value}</span>;
};

interface ColumnDef {
	key: string;
	name: string;
	planId: number;
	price?: number;
}

const PlanColumnHeader = ({
	col,
	isSelected,
	onSelect,
}: {
	col: ColumnDef;
	isSelected: boolean;
	onSelect?: (planId: number) => void;
}) => {
	const className = [
		'plan-features-table__head-cell',
		onSelect && 'plan-features-table__head-cell--clickable',
		isSelected && 'plan-features-table__head-cell--selected',
	]
		.filter(Boolean)
		.join(' ');

	const handleClick = () => onSelect?.(col.planId);
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			onSelect?.(col.planId);
		}
	};

	return (
		<div
			className={className}
			role={onSelect ? 'button' : 'columnheader'}
			tabIndex={onSelect ? 0 : undefined}
			onClick={onSelect ? handleClick : undefined}
			onKeyDown={onSelect ? handleKeyDown : undefined}
			data-qa={`plan-features-table-select-${col.name.toLowerCase()}`}
		>
			<div className="plan-features-table__plan-name">{col.name}</div>
			{col.price != null && (
				<div className="plan-features-table__plan-price">
					<span className="plan-features-table__plan-price-amount">
						${col.price.toFixed(2)}
					</span>
					<span className="plan-features-table__plan-price-period">/mo</span>
				</div>
			)}
		</div>
	);
};

const FeatureRowItem = ({
	item,
	columns,
}: {
	item: FeatureRow;
	columns: ColumnDef[];
}) => (
	<div className="plan-features-table__row" role="row">
		<div
			className="plan-features-table__cell plan-features-table__cell--label"
			role="cell"
		>
			{item.label}
		</div>
		{columns.map(col => (
			<div
				key={col.key}
				className="plan-features-table__cell plan-features-table__cell--value"
				role="cell"
			>
				{renderCell(item[col.key as keyof typeof item] as Cell)}
			</div>
		))}
	</div>
);

const FeatureGroupSection = ({
	group,
	columns,
}: {
	group: FeatureGroup;
	columns: ColumnDef[];
}) => {
	const [expanded, setExpanded] = useState(false);
	const contentId = `plan-features-table-group-${group.category
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')}`;

	const toggle = () => setExpanded(prev => !prev);
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			toggle();
		}
	};

	return (
		<div className="plan-features-table__group">
			<div
				className="plan-features-table__category"
				role="button"
				tabIndex={0}
				aria-expanded={expanded}
				aria-controls={contentId}
				onClick={toggle}
				onKeyDown={handleKeyDown}
				data-qa={`plan-features-table-category-toggle-${group.category
					.toLowerCase()
					.replace(/[^a-z0-9]+/g, '-')}`}
			>
				<div className="plan-features-table__category-label">
					{group.category}
				</div>
				<ChevronIcon direction={expanded ? 'up' : 'down'} />
			</div>
			{expanded && (
				<div id={contentId} className="plan-features-table__group-content">
					{group.items.map(item => (
						<FeatureRowItem key={item.label} item={item} columns={columns} />
					))}
				</div>
			)}
		</div>
	);
};

interface PlanFeaturesTableProps {
	onSelectPlan?: (planId: number) => void;
	selectedPlanId?: number;
}

const PlanFeaturesTable: React.FC<PlanFeaturesTableProps> = ({
	onSelectPlan,
	selectedPlanId,
}) => {
	const [expanded, setExpanded] = useState(false);
	const [settled, setSettled] = useState(false);
	const tableRef = useRef<HTMLDivElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);
	const transitionHandlerRef = useRef<
		((event: TransitionEvent) => void) | null
	>(null);

	useEffect(() => {
		return () => {
			if (contentRef.current && transitionHandlerRef.current) {
				contentRef.current.removeEventListener(
					'transitionend',
					transitionHandlerRef.current
				);
			}
		};
	}, []);

	const columns = COLUMNS.map(col => {
		const plan = BillingPlans.getPlanByPlanId(col.planId);
		return { ...col, price: plan?.Price };
	});

	const toggle = () => {
		setExpanded(prev => {
			const next = !prev;
			if (next) {
				// Settle (drop overflow:hidden) at the end so sticky inside works.
				const onTransitionEnd = (event: TransitionEvent) => {
					if (event.propertyName !== 'max-height') return;
					contentRef.current?.removeEventListener(
						'transitionend',
						onTransitionEnd
					);
					transitionHandlerRef.current = null;
					setSettled(true);
				};
				const el = contentRef.current;
				if (el) {
					if (transitionHandlerRef.current) {
						el.removeEventListener(
							'transitionend',
							transitionHandlerRef.current
						);
					}
					transitionHandlerRef.current = onTransitionEnd;
					el.addEventListener('transitionend', onTransitionEnd);
				}

				// Start the scroll partway through the expand (overlaps animations).
				window.setTimeout(() => {
					const isHorizontalLayout = window.matchMedia(
						'(min-width: 1024px)'
					).matches;
					const planPrice = isHorizontalLayout
						? (document.querySelector(
								'.subscription-card__price'
							) as HTMLElement | null)
						: null;
					(planPrice ?? contentRef.current)?.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
					});
				}, 150);
			} else {
				setSettled(false);
				// max-height collapse transition is 500ms (see SCSS); scroll after.
				window.setTimeout(() => {
					// Find any ancestor of the table that actually scrolls.
					let el: HTMLElement | null = tableRef.current;
					while (el) {
						if (el.scrollHeight > el.clientHeight && el.scrollTop > 0) {
							el.scrollTo({ top: 0, behavior: 'smooth' });
							return;
						}
						el = el.parentElement;
					}
					// Fallback to window
					window.scrollTo({ top: 0, behavior: 'smooth' });
				}, 150);
			}
			return next;
		});
	};

	return (
		<div
			ref={tableRef}
			className={[
				'plan-features-table',
				expanded && 'plan-features-table--open',
				settled && 'plan-features-table--settled',
			]
				.filter(Boolean)
				.join(' ')}
		>
			<div className="plan-features-table__toggle-row">
				<AdlButton
					variant="ghost"
					size="expanded"
					className="plan-features-table__toggle"
					onClick={toggle}
					aria-expanded={expanded}
					aria-controls="plan-features-table-content"
					dataqa="plan-features-table-toggle"
				>
					<span className="plan-features-table__toggle-content">
						Compare All Features
						<ChevronIcon direction={expanded ? 'up' : 'down'} />
					</span>
				</AdlButton>
			</div>

			<div
				ref={contentRef}
				id="plan-features-table-content"
				className="plan-features-table__content"
				aria-hidden={!expanded}
			>
				<div className="plan-features-table__head" role="row">
					<div
						className="plan-features-table__head-cell plan-features-table__head-cell--label"
						role="columnheader"
					>
						<span className="plan-features-table__title">Plan Features</span>
					</div>
					{columns.map(col => (
						<PlanColumnHeader
							key={col.key}
							col={col}
							isSelected={col.planId === selectedPlanId}
							onSelect={onSelectPlan}
						/>
					))}
				</div>

				{PLAN_FEATURES.map(group => (
					<FeatureGroupSection
						key={group.category}
						group={group}
						columns={columns}
					/>
				))}

				<div className="plan-features-table__toggle-row plan-features-table__toggle-row--bottom">
					<AdlButton
						variant="ghost"
						size="expanded"
						className="plan-features-table__toggle"
						onClick={toggle}
						dataqa="plan-features-table-collapse"
					>
						<span className="plan-features-table__toggle-content">
							Compare Plans
							<ChevronIcon direction="up" />
						</span>
					</AdlButton>
				</div>
			</div>
		</div>
	);
};

export default PlanFeaturesTable;
